import { Add } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  InputBase,
  MenuItem,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "store/appHooks";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { createUserCards, fetchUserCards, setCards } from "slices/cardReducer";
import { Controller, useForm } from "react-hook-form";
import { FORM_VALIDATIONS } from "../../constants/formValidation";
import InputWrapper from "./input-wrapper";
import { CustomTextField } from "components/mui-common/CustomTextField";
import SavedAddress from "components/saved-address";
import CardListing from "./cart-listing";
import { getAddress } from "slices/addressReducer";

const stripePromise = loadStripe(
  process.env.REACT_APP_PUBLIC_STRIPE_LIVE_KEY as string
);

const ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#000",
      "::placeholder": {
        color: "#565656B2",
      },
    },
    invalid: {
      color: "#9e2146",
    },
  },
};

const Cards = ({ allowManage = false }: { allowManage?: boolean }) => {
  const dispatch = useAppDispatch();
  const { cardReducer } = useAppSelector((state) => state);
  const [addCard, setAddCard] = useState(false);
  useEffect(() => {
    dispatch(setCards(cardReducer.cards));

    dispatch(fetchUserCards({}));
    dispatch(getAddress());
  }, [dispatch]);

  return (
    <Box
      sx={{
        width: "100%",
        background: {
          md: "#F4F4F4",
          lg: "#F4F4F4",
          xl: "#F4F4F4",
          sm: "",
          xs: "",
          minHeight: "60vh",
        },
        minHeight: "60vh",
      }}
    >
      <CardListing allowManage={allowManage} />

      <Dialog fullWidth maxWidth="md" open={addCard}>
        <DialogTitle>Provide Card Details</DialogTitle>
        <DialogContent>
          <Box>
            <Elements stripe={stripePromise}>
              <InjectedPaymentForm
                onToken={(token: string, name: string, addressId: number) =>
                  dispatch(
                    createUserCards({
                      stripe_card_token: token,
                      card_holder_name: name,
                      address_id: addressId,
                    })
                  )
                }
                onClose={() => setAddCard(false)}
              />
            </Elements>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const InjectedPaymentForm = ({
  onToken,
  onClose,
}: {
  onToken: any;
  onClose?: any | null;
}) => {
  const { addressReducer } = useAppSelector((state) => state);
  const { savedAddress } = addressReducer;
  const { control, handleSubmit } = useForm();
  const element = useElements();
  const stripe = useStripe();
  const [addNewAddress, setAddNewAddress] = useState(false);

  const createPayment = async (data: any) => {
    if (!stripe || !element) {
      return;
    }

    const addressData = savedAddress.find(
      (add: any) => add.id === data.address
    );

    const cardNumber: any = element.getElement(CardNumberElement);

    const { token }: any = await stripe.createToken(cardNumber, {
      name: data.cardholderName,
      address_line1: addressData.address_line_1,
      address_country: addressData.country,
      address_state: addressData.state,
      address_zip: addressData.zipcode,
    });
    onToken(token.id, data.cardholderName, addressData.id);
    onClose();
  };

  return (
    <Box minHeight="40vh" borderTop="1px solid #e9e9e9" pt={2}>
      <form onSubmit={handleSubmit(createPayment)} id="paymentForm">
        <Box>
          <Box mb={2}>
            <Typography mb={1} fontWeight={700}>
              Cardholder Name
            </Typography>

            <Controller
              control={control}
              name="cardholderName"
              rules={{
                ...FORM_VALIDATIONS.REQUIRED,
                ...FORM_VALIDATIONS.ONLY_ALPHABETS,
              }}
              render={({ field: { onChange }, fieldState: { error } }) => (
                <>
                  <InputWrapper width={"90%"} padding="0.3rem 0.7rem">
                    <InputBase
                      placeholder="Enter Name"
                      fullWidth
                      onChange={onChange}
                      error={!!error}
                    />
                  </InputWrapper>
                  {error?.message && (
                    <Typography variant="caption" color="error">
                      {error?.message}
                    </Typography>
                  )}
                </>
              )}
            />
            <Typography fontWeight={700} mb={1} mt={2}>
              Card Details
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <Controller
                control={control}
                name="cardNumber"
                rules={{ ...FORM_VALIDATIONS.REQUIRED }}
                render={({ field: { onChange }, fieldState: { error } }) => (
                  <>
                    <InputWrapper padding={1.5} width={"90%"}>
                      <CardNumberElement
                        onChange={onChange}
                        id="cardNumber"
                        options={ELEMENT_OPTIONS}
                      />
                    </InputWrapper>
                    {error?.message && (
                      <Typography variant="caption" color="error">
                        {error?.message}
                      </Typography>
                    )}
                  </>
                )}
              />

              <Box display="flex" gap={2} width={"100%"}>
                <Controller
                  control={control}
                  name="cardExpiry"
                  rules={{ ...FORM_VALIDATIONS.REQUIRED }}
                  render={({ field: { onChange }, fieldState: { error } }) => (
                    <Box width={"50%"}>
                      <InputWrapper padding={1.5} width={"90%"}>
                        <CardExpiryElement
                          onChange={onChange}
                          id="cardExpiry"
                          options={ELEMENT_OPTIONS}
                        />
                      </InputWrapper>
                      {error?.message && (
                        <Typography variant="caption" color="error">
                          {error?.message}
                        </Typography>
                      )}
                    </Box>
                  )}
                />

                <Controller
                  control={control}
                  name="cardCVV"
                  rules={{ ...FORM_VALIDATIONS.REQUIRED }}
                  render={({ field: { onChange }, fieldState: { error } }) => (
                    <Box width={"50%"}>
                      <InputWrapper padding={1.5} width={"80%"}>
                        <CardCvcElement
                          onChange={onChange}
                          id="cardCVV"
                          options={ELEMENT_OPTIONS}
                        />
                      </InputWrapper>
                      {error?.message && (
                        <Typography variant="caption" color="error">
                          {error?.message}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              </Box>
            </Box>
          </Box>

          <Box mb={2}>
            <Typography fontWeight={700} mb={1}>
              Billing Address
            </Typography>
            {savedAddress.length > 0 && (
              <Controller
                control={control}
                name="address"
                rules={{ ...FORM_VALIDATIONS.REQUIRED }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomTextField
                    select
                    fullWidth
                    size="small"
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message ?? ""}
                    sx={{ width: "90%" }}
                  >
                    {savedAddress.map(
                      (
                        add: {
                          address_line_1: string;
                          id: number;
                          address_line_2: string;
                          city: string;
                          state: string;
                          zipcode: string;
                        },
                        i: number
                      ) => (
                        <MenuItem key={i} value={add.id}>
                          {add.address_line_1}
                          {`${
                            add.address_line_2 ? ` ,${add.address_line_2}` : ``
                          }`}
                          {add.city},{add.state},{add.zipcode}
                        </MenuItem>
                      )
                    )}
                  </CustomTextField>
                )}
              />
            )}

            <Button startIcon={<Add />} onClick={() => setAddNewAddress(true)}>
              Add new address
            </Button>
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={1} mt={5}>
            <Button variant="contained" onClick={onClose}>
              Close
            </Button>
            <Button
              style={{
                color: "#FFF",
                background: "#FE860A",
              }}
              variant="contained"
              type="submit"
            >
              Add Card
            </Button>
          </Box>
        </Box>
      </form>

      {addNewAddress && (
        <Dialog
          open
          maxWidth="lg"
          fullWidth
          onClose={() => setAddNewAddress(false)}
        >
          <DialogContent>
            <SavedAddress />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default Cards;
